import React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";
import { Trans } from "@lingui/react";
import {
  SUPPORTED_LOCALES,
  LOCALE_NAMES,
  isSupportedLocale,
  type SupportedLocale,
} from "../../i18n/i18n";
import { useLocale } from "../../i18n/I18nProvider";

interface LocalePickerProps {
  /** When true, renders a bare Select without FormControl/InputLabel (for header bars). */
  compact?: boolean;
  /** Additional sx styles forwarded to the Select (compact) or FormControl (default). */
  sx?: SxProps<Theme>;
}

/**
 * Locale picker dropdown component.
 *
 * Displays all seven supported locales with their native-language names
 * and triggers a runtime locale switch (no page reload) on selection.
 *
 * Satisfies:
 * - STORY-FRONTEND-I18N-AC-003 (runtime locale switching)
 * - TASK-FRONTEND-I18N-PICKER technical criteria (keyboard accessible, highlights active)
 */
export function LocalePicker({ compact = false, sx }: LocalePickerProps): React.ReactElement {
  const { locale, setLocale } = useLocale();

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (isSupportedLocale(value)) {
      void setLocale(value);
    }
  };

  const menuItems = SUPPORTED_LOCALES.map((loc: SupportedLocale) => (
    <MenuItem key={loc} value={loc}>
      {LOCALE_NAMES[loc]}
    </MenuItem>
  ));

  if (compact) {
    return (
      <Select
        id="locale-picker"
        value={locale}
        onChange={handleChange}
        size="small"
        inputProps={{ "aria-label": "Select language" }}
        sx={{ minWidth: 120, "& .MuiSelect-select": { py: 0.75, px: 1.5 }, ...sx as object }}
      >
        {menuItems}
      </Select>
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 140, ...sx as object }}>
      <InputLabel id="locale-picker-label">
        <Trans id="localePicker.label" />
      </InputLabel>
      <Select
        labelId="locale-picker-label"
        id="locale-picker"
        value={locale}
        label={<Trans id="localePicker.label" />}
        onChange={handleChange}
        inputProps={{
          "aria-label": "Select language",
        }}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
}

export default LocalePicker;
